import { lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ActionButton } from "@wine2trace/ui/FormComponents";
import { ViewSwitch } from "../FormComponents/Switch";
import useLabels from "../../hooks/useLabels";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../routeAccess";
import PageHeader from "../PageHeader";
import SecondaryBar from "../SecondaryBar";
import {
  selectConfigs,
  selectTankDisplayGrid,
  selectTanks,
  selectUser,
} from "../../context/selectors";
import { toggleTanksDisplayGrid } from "../../context/configs/configsSlice";
import { resolveTankUnit } from "../../services/tankUnit";
import { SkeletonTable } from "../Skeleton/Skeleton";
import { summarizeTanks } from "../TankList/tankRows";
import { formatTankVolume } from "../TankList/tankCells";

import "./styles/index.scss";

const TankList = lazy(() => import("../TankList"));

// Tanks page: title with the number of tanks and how full they are, view switch (table | cards), "new tank", then the list.
const Tanks = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const tanks = useSelector(selectTanks);
  const configs = useSelector(selectConfigs);
  const user = useSelector(selectUser);
  const displayModeGrid = useSelector(selectTankDisplayGrid);

  const unit = resolveTankUnit(user?.settings?.tank_unit);
  const locale = configs?.locale?.replace("_", "-");
  const summary = summarizeTanks(tanks);

  const facts = [
    getLabel("lotsTotalCount", { count: summary.count }),
    summary.capacity > 0 &&
      getLabel("tanksVolumeSummary", {
        quantity: formatTankVolume(summary.quantity, unit, locale),
        capacity: formatTankVolume(summary.capacity, unit, locale),
        percent: summary.percent,
      }),
  ];

  return (
    <div className="primary_container">
      <SecondaryBar breadCrumb={[getLabel("tanksNavLink")]} />
      <PageHeader title={getLabel("tanks")} facts={summary.count > 0 ? facts : []}>
        {!isMobile && (
          <ViewSwitch
            status={displayModeGrid}
            toggleHandler={() => dispatch(toggleTanksDisplayGrid())}
          />
        )}
        {can(...PERMISSIONS.tanksWrite) && (
          <ActionButton to="/tanks/new" variant="primary" icon="fa-plus">
            {getLabel("createTank")}
          </ActionButton>
        )}
      </PageHeader>
      <Suspense fallback={<SkeletonTable colCount={7} isMobile={isMobile} />}>
        <TankList isMobile={isMobile} />
      </Suspense>
    </div>
  );
};

export default Tanks;
